/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Generic functions
 */
import { PublicClientApplication } from '@azure/msal-browser';
// eslint-disable-next-line import/no-unresolved
import { encode } from 'base-64';
import _ from 'lodash';
import { marked } from 'marked';
import { toJS } from 'mobx';
import moment from 'moment';
import utf8 from 'utf8';
import { v4 as uuidv4 } from 'uuid';
import { msalConfig } from '../authConfig';
import * as constants from './constants';
import env from './env';
import TokenHelper from './TokenHelper';

export default class utils {
  static convertSecondstoHoursandMin(readingTime) {
    const hours = Math.floor(readingTime / 3600);
    const min = Math.round((readingTime % 3600) / 60);

    return {
      hours,
      min
    };
  }

  static convertDate(date) {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 10, 30, 0);
  }

  static epochToUTC(epochTimestamp) {
    const date = new Date(epochTimestamp * 1000).toUTCString().replace('GMT', 'UTC');
    return date;
  }

  static updateCookie(cookieName, secret, expires) {
    return `${cookieName}=${secret}; expires=${expires}; path=/; domain=.pearson.com; Secure;`;
  }

  static getMsalSessionToken() {
    try {
      let keys = window.sessionStorage.getItem(`${'msal.token.keys.'}${env.INT_AZURE_CLIENT_ID}`);
      keys = JSON.parse(keys).accessToken;
      if (keys !== null) {
        const msalToken = window.sessionStorage.getItem(keys);
        return JSON.parse(msalToken);
      }
      return null;
    } catch (error) {
      console.error(error?.message);
      return null;
    }
  }

  static setCookie() {
    const msalToken = this.getMsalSessionToken();
    if (msalToken !== null) {
      const { secret, expiresOn } = msalToken;
      const expires = utils.epochToUTC(expiresOn);
      document.cookie = this.updateCookie(constants.AZURE_COOKIE_NAME, secret, expires);
    }
  }

  static setCDNTokenInCookie(str) {
    const epochTime = str?.value?.split('~');
    const expiresOn = epochTime[0].split('=')[1];
    const expires = utils.epochToUTC(expiresOn);
    document.cookie = this.updateCookie(str.name, str.value, expires);
  }

  static getValidCdnTokenFromCookie(bufferMinutes = 10) {
    const cookieName = constants.ETEXT_CDN_TOKEN_COOKIE_NAME;
    const cookies = document.cookie;
    if (!cookies) return null;

    const prefix = `${cookieName}=`;
    const entry = cookies.split('; ').find((cookie) => cookie.startsWith(prefix));
    if (!entry) return null;

    const value = entry.slice(prefix.length);
    const match = value.match(/exp=(\d+)/);
    if (!match) return null;

    const expiresAtMs = Number(match[1]) * 1000;
    if (expiresAtMs - bufferMinutes * 60 * 1000 <= Date.now()) return null;

    return value;
  }

  static cookieExists(cookieName) {
    const cookies = document.cookie;
    if (!cookies) {
      return false;
    }
    const cookieArray = cookies.split('; ');
    // eslint-disable-next-line
    for (let cookie of cookieArray) {
      const [name] = cookie.split('=');
      if (name === cookieName) {
        return true;
      }
    }

    return false;
  }

  static getCookieByName(cookieName) {
    const cookies = document.cookie;
    if (!cookies) {
      return null;
    }

    const cookieArray = cookies.split('; ');
    // eslint-disable-next-line
    for (let cookie of cookieArray) {
      const [name, value] = cookie.split('=');
      if (name === cookieName) {
        return decodeURIComponent(value);
      }
    }

    return null;
  }

  static getToken() {
    let keys = window.sessionStorage.getItem(`${'msal.token.keys.'}${env.INT_AZURE_CLIENT_ID}`);
    keys = JSON.parse(keys).accessToken;
    if (keys !== null) {
      let value = window.sessionStorage.getItem(keys);
      value = JSON.parse(value);
      const { secret } = value;
      return secret;
    }
    return null;
  }

  static getStackBParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      isStackB: urlParams.get('stackB') === 'true',
      env: urlParams.get('env')
    };
  }

  static async getStackBHeaders(existingHeaders, envParam) {
    const forProd = envParam === constants.ENV_PROD;
    const token = forProd ? await TokenHelper.getSys2sysToken(forProd) : this.getToken();

    return {
      ...existingHeaders,
      Authorization: `Bearer ${token}`
    };
  }

  /**
   * URL Stack B flag, optionally OR'd with a local toggle (e.g. testbed checkbox).
   * With no arguments, returns the URL flag only.
   */
  static getEffectiveStackB(localStackB) {
    const { isStackB } = this.getStackBParams();
    if (arguments.length === 0) {
      return isStackB;
    }
    return isStackB || localStackB;
  }

  /**
   * When Stack B is active (from URL and/or optional local toggle), apply Stack B bearer headers; otherwise return headers unchanged.
   */
  static async resolveStackBHeaders(existingHeaders, localStackB) {
    const { isStackB, env: stackBEnvParam } = this.getStackBParams();
    const effectiveStackB = arguments.length >= 2 ? isStackB || localStackB : isStackB;
    if (!effectiveStackB) {
      return existingHeaders;
    }
    return this.getStackBHeaders(existingHeaders, stackBEnvParam);
  }

  /**
   * Direct Berlin/MCP axios calls: Stack B uses interactive token via getStackBHeaders; otherwise sys2sys.
   */
  static async resolveBerlinDirectAuthHeaders(baseHeaders, localStackB) {
    const { isStackB, env: stackBEnvParam } = this.getStackBParams();
    const effectiveStackB = arguments.length >= 2 ? isStackB || localStackB : isStackB;
    if (effectiveStackB) {
      return this.getStackBHeaders(baseHeaders, stackBEnvParam);
    }
    const token = await TokenHelper.getSys2sysToken(false);
    return { ...baseHeaders, Authorization: `Bearer ${token}` };
  }

  /** Append stackB/env query params when the SPA is in Stack B mode (e.g. testbed launch links). */
  static appendStackBQueryToPathIfNeeded(path) {
    const { isStackB, env: stackBEnv } = this.getStackBParams();
    if (!isStackB || path.includes('stackB=true')) {
      return path;
    }
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}stackB=true${stackBEnv ? `&env=${encodeURIComponent(stackBEnv)}` : ''}`;
  }

  /** In-app route suffix such as `?stackB=true&env=stg` for feedback/observe links. */
  static getStackBSpaQuerySuffix() {
    const { isStackB, env: stackBEnv } = this.getStackBParams();
    if (!isStackB) {
      return '';
    }
    return `?stackB=true${stackBEnv ? `&env=${stackBEnv}` : ''}`;
  }

  static uuid() {
    return uuidv4();
  }

  static convertBoolean(str) {
    let bool = false;
    if (str && str !== null && str !== '') {
      const lowerStr = str.toLowerCase();
      if (lowerStr === 'y') {
        bool = true;
      }
    }
    return bool;
  }

  static async redirectToLogin() {
    try {
      document.cookie = this.updateCookie(constants.AZURE_COOKIE_NAME, '', constants.DEFAULT_COOKIE_EXPIRES);
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      window.history.replaceState(null, '', window.location.href);

      window.sessionStorage.clear();
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.href,
        account: msalInstance.getActiveAccount()
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during MSAL initialization or logout:', error);
    }
  }

  static processStoreValue = (chatgptResponseUtilityStore) => {
    /* eslint-disable camelcase */
    let rowProcessed = {};
    const {
      summary,
      quiz,
      discuss,
      chat_enrichment,
      chat_intent,
      content_classifier,
      flashcards,
      topic,
      summary_enrichment,
      summaryTopic,
      quizValidateIntent,
      quizIntent,
      mcq,
      mcqMisconceptions,
      mcqSkillIdentifier,
      mcqQuestionGeneration,
      quizMcq,
      quizFreeResponse,
      freeResponse,
      mcqDistract,
      dictionarySummary,
      dictionaryQuiz,
      content_filter
    } = chatgptResponseUtilityStore;
    if (chatgptResponseUtilityStore) {
      const dictionarySummaryData = dictionarySummary && dictionarySummary.length > 0 ? dictionarySummary : [];
      const dictionaryQuizData = dictionaryQuiz && dictionaryQuiz.length > 0 ? dictionaryQuiz : [];
      const summaryData = summary && summary.length > 0 ? summary : [];
      const quizData = quiz && quiz.length > 0 && quiz ? quiz : [];
      const discussData = discuss && discuss.length > 0 && discuss ? discuss : [];
      const chatEnrichmentData = chat_enrichment && chat_enrichment.length > 0 ? chat_enrichment : [];
      const chatIntentData = chat_intent && chat_intent.length > 0 ? chat_intent : [];
      const flashcardsData = flashcards && flashcards.length > 0 ? flashcards : [];
      const topicData = topic && topic.length > 0 ? topic : [];
      const summaryEnrichmentData = summary_enrichment && summary_enrichment.length > 0 ? summary_enrichment : [];
      const summaryTopicData = summaryTopic && summaryTopic.length > 0 ? summaryTopic : [];
      const contentClassifier = content_classifier && content_classifier.length > 0 ? content_classifier : [];
      const quizValidateData = quizValidateIntent && quizValidateIntent.length > 0 ? quizValidateIntent : [];
      const quizIintentData = quizIntent && quizIntent.length > 0 ? quizIntent : [];
      const mcqData = mcq && mcq.length > 0 ? mcq : [];
      const mcqMisconceptionsData = mcqMisconceptions && mcqMisconceptions.length > 0 ? mcqMisconceptions : [];
      const mcqSkillIdentifierData = mcqSkillIdentifier && mcqSkillIdentifier.length > 0 ? mcqSkillIdentifier : [];
      const mcqQuestionGenerationData =
        mcqQuestionGeneration && mcqQuestionGeneration.length > 0 ? mcqQuestionGeneration : [];
      const quizMcqData = quizMcq && quizMcq.length > 0 ? quizMcq : [];
      const quizFreeResponseData = quizFreeResponse && quizFreeResponse.length > 0 ? quizFreeResponse : [];
      const freeResponseData = freeResponse && freeResponse.length > 0 ? freeResponse : [];
      const mcqDistractData = mcqDistract && mcqDistract.length > 0 ? mcqDistract : [];
      const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
      const combineData = [
        ...dictionarySummaryData,
        ...dictionaryQuizData,
        ...summaryData,
        ...discussData,
        ...quizData,
        ...chatEnrichmentData,
        ...chatIntentData,
        ...flashcardsData,
        ...topicData,
        ...contentClassifier,
        ...summaryEnrichmentData,
        ...summaryTopicData,
        ...quizValidateData,
        ...quizIintentData,
        ...mcqData,
        ...mcqMisconceptionsData,
        ...mcqSkillIdentifierData,
        ...mcqQuestionGenerationData,
        ...quizMcqData,
        ...quizFreeResponseData,
        ...freeResponseData,
        ...mcqDistractData,
        ...contentFilterData
      ];
      /* eslint-disable no-param-reassign */
      if (combineData[0] && combineData[0].type === 'chat_intent') {
        try {
          combineData[0].botResponse = JSON.parse(combineData[0].botResponse);
        } catch (err) {
          const { botResponse } = combineData[0];
          combineData[0] = { ...combineData[0], botResponse: botResponse[0] };
        }
      }
      const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
      // const { prompt } = combineDatatoJS[0];
      if (combineDatatoJS[0] && Object.keys(combineDatatoJS[0]).length > 0) {
        rowProcessed = Object.entries(combineDatatoJS[0]).reduce((obj, [key, value]) => {
          if (key !== null && value !== null) {
            obj[key] = value;
          }
          return obj;
        }, {});
      }
    }
    if (rowProcessed.type === 'chat_intent') {
      const { botResponse } = rowProcessed;
      let processBotResponse = '';
      try {
        processBotResponse = [JSON.parse(botResponse)];
      } catch (err) {
        const splitTwoText = botResponse.split('[BAD-GPT]');
        const normGPT = JSON.parse(splitTwoText[0].split('[NORM-GPT]')[1]);
        const badGPT = JSON.parse(splitTwoText[1]);
        processBotResponse = [normGPT, badGPT];
      }
      rowProcessed.botResponse = processBotResponse;
    }
    return rowProcessed;
  };

  static displayUserId = (data) => {
    if (data.userId === constants.SIGNED_OUT_USER_ID) {
      return constants.SIGNED_OUT_USER;
    }
    return data.userId;
  };

  static processChannelsDataResponse = (channelsUtility) => {
    let rowProcessed = {};
    const { content_filter, channelsResult } = channelsUtility;
    if (channelsUtility) {
      const channelsResultData = channelsResult && channelsResult.length > 0 ? channelsResult : [];
      const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
      const combineData = [...channelsResultData, ...contentFilterData];
      const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
      /* eslint-disable no-param-reassign */
      if (combineDatatoJS[0] && Object.keys(combineDatatoJS[0]).length > 0) {
        rowProcessed = Object.entries(combineDatatoJS[0]).reduce((obj, [key, value]) => {
          if (key !== null && value !== null) {
            obj[key] = value;
          }
          return obj;
        }, {});
      }
    }
    return rowProcessed;
  };

  static processChannelsSubTypeResponse = (channelsSubType) => {
    let returnVal = [];
    const { content_filter, channelsResult } = channelsSubType;
    if (channelsSubType) {
      const channelsResultData = channelsResult && channelsResult.length > 0 ? channelsResult : [];
      const contentFilterData = content_filter && content_filter.length > 0 ? content_filter : [];
      const combineData = [...channelsResultData, ...contentFilterData];
      returnVal = JSON.parse(JSON.stringify(toJS(combineData)));
    }
    return returnVal;
  };

  static weeksStartEndDate = (month, monthIndex, year) => {
    const getMomentDate = (start, end, index) => {
      const startDate = moment([year, monthIndex - 1, start]).format(constants.MM_DD_YYYY_FORMAT);
      const endDate = moment([year, monthIndex - 1, end]).format(constants.MM_DD_YYYY_FORMAT);
      const currentDate = moment().format(constants.MM_DD_YYYY_FORMAT);
      return {
        startDate,
        endDate,
        weekNumber: `Week ${index + 1}`,
        month: moment([year, monthIndex - 1, start]).format(constants.MMMM_FORMAT),
        disable: moment().year() === year && endDate >= currentDate
      };
    };
    const weekStartEndDay = [];
    const first = month.day() === 0 ? 6 : month.day() - 1;
    let day = 7 - first;
    const last = month.daysInMonth();
    const count = (last - day) / 7;
    weekStartEndDay.push(getMomentDate(1, day, 0));
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < count; i++) {
      weekStartEndDay.push(getMomentDate(day + 1, Math.min((day += 7), last), i + 1));
    }
    return weekStartEndDay;
  };

  static getSessionData = () => {
    try {
      const keys = window.sessionStorage.getItem('msal.account.keys');
      if (keys && keys !== '[]') {
        const key = keys.replace(/[,"[\]]/g, '');
        const value = JSON.parse(window.sessionStorage.getItem(key));
        return value?.username || null;
      }
    } catch (error) {
      console.error('Error reading session data:', error);
    }
    return null;
  };

  static containsMathML = (str) => {
    const mathmlRegex = /<math[\s\S]*?<\/math>/gi; // Regex to match <math> ... </math> tags
    return mathmlRegex.test(str);
  };

  static loadMathJax = () => {
    const script = document.createElement('script');
    const scriptMathConfig = document.createElement('script');
    script.type = 'text/javascript';
    scriptMathConfig.type = 'text/x-mathjax-config';
    script.src = constants.MATHJAX_CDN_URL;
    document.getElementsByTagName('head')[0].appendChild(script);
    document.getElementsByTagName('head')[0].appendChild(scriptMathConfig);
    script.onload = () => {
      // Set to false if math elements should not be included in the tabindex
      window.MathJax.Hub.config.menuSettings.inTabOrder = false;
    };
    scriptMathConfig.onload = () => {
      window.MathJax.Hub.Config({
        TeX: {
          extensions: ['mhchem3/mhchem.js'],
          Macros: {
            texttt: ['{\\mathrm{#1}}', 1]
          }
        },
        tex2jax: {
          inlineMath: [
            ['$', '$'],
            ['\\\\(', '\\\\)']
          ],
          displayMath: [
            ['$$', '$$'],
            ['\\\\[', '\\\\]']
          ],
          processEscapes: true,
          ignoreClasses: ['no-mathjax']
        },
        loader: { load: ['[tex]/mhchem', '[tex]/mathtools'] },
        tex: { packages: { '[+]': ['mhchem', 'mathtools'] } }
      });
    };
  };

  static loadMathJaxV3 = () => {
    if (window.MathJax && window.MathJax.version && window.MathJax.version.indexOf('3') !== 0) {
      window.MathJax = '';
    }
    if (!(window.MathJax && window.MathJax.startup) && typeof window.isMathjaxLoaded === 'undefined') {
      window.isMathjaxLoaded = true;
      // Create MathJax config
      const mathJaxConfigScript = document.createElement('script');
      mathJaxConfigScript.text = `
          window.MathJax = {
            loader: { load: ['[tex]/mhchem', '[tex]/mathtools', '[tex]/noerrors', 'input/tex', 'input/asciimath', 'output/chtml'] },
            startup: {
              ready() {
                const { HTMLDocument } = MathJax._.handlers.html.HTMLDocument;
                HTMLDocument.prototype.findPosition = function (N, index, delim, nodes) {
                  const adaptor = this.adaptor;
                  const inc = 1 / (nodes[N].length || 1);
                  let i = N;
                  for (const [node, n] of nodes[N]) {
                    if (index <= n && adaptor.kind(node) === '#text') {
                      return { i, node, n: Math.max(index, 0), delim };
                    }
                    index -= n;
                    i += inc;
                  }
                  return { node: null, n: 0, delim };
                };
                MathJax.startup.defaultReady();
              }
            },
            asciimath: { delimiters: [['\\\\$$', '\\\\$$']] },
            tex: {
              inlineMath: [['$', '$'], ['\\\\(', '\\\\)'], ['\\\\[', '\\\\]']],
              packages: { '[+]': ['mhchem', 'mathtools'] },
              macros: { prescript: ['{}^{#1}_{#2}', 2] }
            }
          };
        `;
      // Append config to head
      document.head.appendChild(mathJaxConfigScript);
      // Dynamically load MathJax script
      const mathJaxScript = document.createElement('script');
      mathJaxScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-mml-chtml.min.js';
      document.head.appendChild(mathJaxScript);
    }
  };

  static processMath = (element) => {
    if (typeof window.MathJax !== 'undefined' && typeof window.MathJax.Hub !== 'undefined') {
      if (element) {
        try {
          window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, element]);
        } catch (err) {
          // MathJax internals (e.g. asciimath.js) may not be fully ready yet — retry after a short delay.
          // Prevents an uncaught TypeError from blanking the detail page while still ensuring math renders.
          setTimeout(() => {
            try {
              window.MathJax?.Hub?.Queue?.(['Typeset', window.MathJax.Hub, element]);
            } catch (retryErr) {
              console.error('MathJax typeset failed after retry:', retryErr?.message || retryErr);
            }
          }, 300);
        }
      }
    } else {
      // Load MathJax dynamically
      utils.loadMathJax();
    }
  };

  static containsMarkDownText = (res) => {
    const str = typeof res === 'string' ? res : res.toString();
    // Regular expressions for common Markdown patterns
    const markdownPatterns = [
      /#\s/, // Headers (e.g., # Header)
      /\[.*?\]\(.*?\)/, // Links (e.g., [link](http://example.com))
      /\*\*.*?\*\*/, // Bold (e.g., **bold text**)
      /__.*?__/, // Bold (e.g., __bold text__)
      /\*.*?\*/, // Italic (e.g., *italic text*)
      /_.*?_/, // Italic (e.g., _italic text_)
      /^\s*[-*+]\s+/, // Unordered lists (e.g., - item)
      /^\s*\d+\.\s+/, // Ordered lists (e.g., 1. item)
      /```/, // Code blocks (e.g., ```code``` for inline code blocks)
      /~~/, // Strikethrough (e.g., ~~strikethrough~~)
      /!\[.*?\]\(.*?\)/ // Images (e.g., ![alt text](url))
    ];

    return markdownPatterns.some((pattern) => pattern.test(str));
  };

  static rendermath = async (text) => {
    // Create a temporary container element
    const tempContainer = document.createElement('span');
    // Set the content (string with math expressions) as innerHTML
    tempContainer.innerHTML = text;
    if (!window.MathJax || !window.MathJax.typesetPromise) {
      // console.log('Windows.MathJax not available');
      await utils.loadMathJaxV3();
    }
    await window.MathJax.typesetPromise([tempContainer]);
    return tempContainer;
  };

  static convertToMarkedDownString = (content) => {
    const str = typeof content === 'string' ? content : content.toString();
    const formattedText = str.replace(/(\n\s*){3,}/g, '\n\n');
    const parsedData = marked
      .parse(formattedText.replaceAll('`', '@@').replaceAll('\\', '@!'))
      .replaceAll('<em>', '_')
      .replaceAll('</em>', '_');
    return parsedData.replaceAll('@@', '`').replaceAll('@!', '\\');
  };

  static textFromContentV3 = (content) =>
    new Promise((resolve, reject) => {
      if (
        (content.indexOf('`') >= 0 &&
          content.lastIndexOf('`') > 0 &&
          content.indexOf('`') !== content.lastIndexOf('`')) ||
        content.indexOf('\\') >= 0 ||
        content.includes('</math>') ||
        content.indexOf('$') !== content.lastIndexOf('$')
      ) {
        // Create a temporary container element
        const tempContainer = document.createElement('span');
        // Set the content (string with math expressions) as innerHTML
        tempContainer.innerHTML = content;
        // Use MathJax.typesetPromise() to typeset only this container
        window.MathJax.typesetPromise([tempContainer])
          .then(() => {
            resolve(tempContainer.innerHTML); // Resolve the promise with the container's typeset HTML
            // Remove the temporary container after typesetting is done
            tempContainer.remove();
          })
          .catch((err) => {
            reject(err); // Reject the promise if an error occurs
          });
      } else {
        resolve(content);
      }
    });

  static convertToMarkedDownStringV3 = async (content) => {
    try {
      const checkCharExist = typeof content === 'string' && content && content.trim();
      if (checkCharExist && checkCharExist.length > 0) {
        return marked.parse(content);
      }
      return content;
    } catch (e) {
      return content;
    }
  };

  /* eslint-disable */
  static convertServerDateTime = (dateTime) => {
    let convertSecondsToDate = dateTime;
    if (typeof dateTime === 'string') {
      if (new Date(parseInt(dateTime)).getFullYear() === 1970) {
        convertSecondsToDate = new Date(parseInt(dateTime) * 1000).toLocaleString();
      } else {
        convertSecondsToDate = new Date(parseInt(dateTime)).toLocaleString();
      }
    } else {
      if (new Date(dateTime).getFullYear() === 1970) {
        convertSecondsToDate = new Date(dateTime * 1000).toLocaleString();
      } else {
        convertSecondsToDate = new Date(dateTime).toLocaleString();
      }
    }
    return convertSecondsToDate;
  };

  static getTenantId = (tenantName, tenantConfig) => {
    let tenTId = '';
    const { data } = toJS(tenantConfig);
    if (data.length > 0) {
      const match = data.find((config) => config.tenantName === tenantName);
      if (match) {
        tenTId = match.tenantId;
      }
    }

    return tenTId;
  };

  static getObjectName = (array, targetName) => {
    const matchObjects = array.filter((obj) => obj.clusterName === targetName);
    return matchObjects.map(
      ({ serverDateTime, userCommand, dateStamp, bookId, userId, title, type, requestId, channelsId }) => ({
        serverDateTime,
        userCommand,
        dateStamp,
        bookId,
        userId,
        type,
        title,
        requestId,
        channelsId
      })
    );
  };

  static constructPayload = (fields) => {
    const payload = {};
    Object.entries(fields).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(typeof value === 'string' && value.trim() === '')
      ) {
        payload[key] = value;
      }
    });
    return payload;
  };

  /**
   * For each numeric index in `indices`, looks up values in `record`, sorts by index ascending,
   * and returns pairs. Omits missing, null, or empty string values.
   * Supports plain objects and Map-like objects (e.g. MST types.map / ObservableMap with .get).
   * @param {Array<number|string>|null|undefined} indices
   * @param {object|null|undefined} record Plain object or Map-like (.get)
   * @returns {{ index: number, value: string }[]}
   */
  static mapSortedIndicesToRecordValues(indices, record) {
    if (!indices?.length) return [];
    if (!record || typeof record !== 'object' || Array.isArray(record)) return [];
    const pick = (idx) => {
      const k = String(idx);
      if (typeof record.get === 'function') {
        return record.get(k) ?? record.get(idx);
      }
      return record[k] ?? record[idx];
    };
    return indices
      .slice()
      .map(Number)
      .sort((a, b) => a - b)
      .map((idx) => {
        const value = pick(idx);
        return value != null && value !== '' ? { index: idx, value } : null;
      })
      .filter(Boolean);
  }

  static buildRequestPartsFromPayload = (event) => {
    const payload = event.payload?.tenantId ? _.omit(event.payload, constants.TENANT_ID) : event.payload;
    let headers = event.payload?.tenantId ? { [constants.BERLIN_TENANT_ID_HEADER]: event.payload?.tenantId } : {};
    headers = Object.keys(headers).length > 0 ? headers : undefined;
    return { payload, headers };
  };

  static buildHeadersWithTenantId = (event) => {
    let headers = event.tenantId ? { [constants.BERLIN_TENANT_ID_HEADER]: event.tenantId } : {};
    headers = Object.keys(headers).length > 0 ? headers : undefined;
    return headers;
  };

  static handleAscending = (dir, value, data) => {
    if (dir === 'asc') {
      const sorted = [...data].sort((a, b) => (a[value] > b[value] ? 1 : -1));
    } else {
      const sorted = [...data].sort((a, b) => (a[value] < b[value] ? 1 : -1));
    }
    return sorted;
  };

  static mapTitlesByTenant = (dataList, sortedData, tenantName) => {
    const idFieldByTenant = {
      [constants.CHANNELS]: 'channelsId',
      [constants.AI_STUDY_TOOL]: 'bookId',
      [constants.PVS]: 'bookId'
    };

    if (!Array.isArray(dataList) || !Array.isArray(sortedData)) {
      return sortedData;
    }

    const idField = idFieldByTenant[tenantName];

    const valueMap = new Map();

    dataList.forEach((value) => {
      const key = value[idField];
      if (key) {
        valueMap.set(key, value.title);
      }
    });

    return sortedData.map((dataValue) => {
      const key = dataValue[idField];
      if (valueMap.has(key)) {
        return { ...dataValue, title: valueMap.get(key) };
      }
      return dataValue;
    });
  };

  static renderLinksVideos = (text) => {
    const parseAttributes = (attributes) => {
      const attrs = [];
      const regex = /(\w+)="([^"]+)"/g;
      let match = regex.exec(attributes);

      while (match !== null) {
        attrs.push(` ${match[1]}="${match[2]}"`);
        match = regex.exec(attributes);
      }

      return attrs.join('');
    };

    const linkRegex = /<a href="([^"]+)"([^>]*)>(.*?)<\/a>/g;
    const videoTagRegex = /<video([^>]+)>/g;
    const imgTagRegex = /<img[^>]*>/g;
    const iframeRegex = /<iframe[^>]*>/g;
    let finalRespStr = text;
    if (linkRegex.test(finalRespStr)) {
      finalRespStr = finalRespStr?.replace(linkRegex, (match, url, attributes, linkText) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer"${parseAttributes(
          attributes
        )}>${linkText}</a>`;
      });
    }

    if (imgTagRegex.test(finalRespStr)) {
      finalRespStr = finalRespStr?.replace(imgTagRegex, (imgTag) => {
        if (!imgTag.includes('width=')) {
          return imgTag?.replace('<img', '<img style="width: 100%;"');
        }
        return imgTag;
      });
    }

    if (iframeRegex.test(finalRespStr)) {
      finalRespStr = finalRespStr?.replace(iframeRegex, (iframeTag) => {
        if (!iframeTag.includes('width=')) {
          return iframeTag?.replace(
            '<iframe',
            '<iframe allowfullscreen style="width: 100%; height: 500px; border: 1px solid;"'
          );
        }
        return iframeTag;
      });
    }

    if (videoTagRegex.test(finalRespStr)) {
      return finalRespStr?.replace(videoTagRegex, (match) => {
        const hasStyle = /style="[^"]+"/.test(match);
        const hasControls = /controls/.test(match);
        let enhancedTag = match;
        if (!hasStyle) {
          enhancedTag = enhancedTag?.replace('<video', '<video style="width: 100%;"');
        }
        if (!hasControls) {
          enhancedTag = enhancedTag?.replace('<video', '<video controls');
        }
        return enhancedTag;
      });
    }
    return finalRespStr;
  };

  static getObserveSearchErrorMessageForEntityType(entityType) {
    if (entityType == null || String(entityType).trim() === '') {
      return '';
    }
    return `Enter a ${entityType}`;
  }

  static generateErrorMessage = (tenantName, observeEntityTypeLabel, observeEntityIdentifierVariant) => {
    if (observeEntityIdentifierVariant === 'CustomTextField') {
      const entityTypeMessage = utils.getObserveSearchErrorMessageForEntityType(observeEntityTypeLabel);
      if (entityTypeMessage) {
        return entityTypeMessage;
      }
    }
    return constants.ERROR_MESSAGE_TEMPLATE.replace('{{placeholder}}', constants.placeholders[tenantName] || 'Item');
  };

  /**
   * True when Observe entity type uses book/course/channel autocomplete selection.
   * False for graphId/requestId-only entity types (no bookId selection required).
   */
  static observeEntityTypeRequiresBookSelection(entityIdentifierConfig, entityType) {
    if (!entityIdentifierConfig?.variants) {
      return true;
    }
    const variant = entityIdentifierConfig.variants[entityType] || {};
    if (variant.identifierSource?.graph) {
      return false;
    }
    if (variant.fetchBookTitles === false) {
      return false;
    }
    const normalizedEntityType = String(entityType || '').toLowerCase();
    if (
      normalizedEntityType === constants.OBSERVE_ENTITY_GRAPHS ||
      normalizedEntityType === constants.OBSERVE_ENTITY_GRAPH
    ) {
      return false;
    }
    if (variant.component === constants.CUSTOM_TEXT_FIELD_COMPONENT) {
      const searchField = variant.searchField || '';
      if (
        searchField === constants.OBSERVE_SEARCH_FIELD_GRAPH_ID ||
        searchField === constants.OBSERVE_SEARCH_FIELD_REQUEST_ID
      ) {
        return false;
      }
    }
    return variant.component === constants.AUTOCOMPLETE_COMPONENT || !variant.component;
  }

  static getDate = () => {
    const date = new Date();
    const day = date.getUTCDate().toString().padStart(2, '0');
    const monthShort = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const year = date.getUTCFullYear().toString().slice(-2);
    return `${day}${monthShort}${year}`;
  };

  static sanitizeFilenamePart = (value) => {
    const sanitized = String(value || 'export')
      .replace(/[^\w.-]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return sanitized || 'export';
  };

  static getAuditExportFilename = (tenantName, exportModule) =>
    `${utils.sanitizeFilenamePart(tenantName)}_${utils.sanitizeFilenamePart(exportModule)}_${utils.getDate()}.xlsx`;

  static isValidSearchFieldValue = (key, value, specialKey) => {
    if (key === specialKey) {
      return value === true;
    }
    return !_.isNil(value) && !_.isEmpty(value);
  };

  static isGraphIdentifierVariant = (variantConfig) => variantConfig?.identifierSource?.graph === true;

  /** Whether an autocomplete option matches a default, URL, or prefill value (ID fields first, title optional). */
  static autocompleteOptionMatchesValue = (item, value, { includeTitle = true } = {}) => {
    if (value == null || value === '') return false;
    const normalized = typeof value === 'string' ? value : String(value);
    if (item?.graphId === normalized || item?.bookId === normalized || item?.channelsId === normalized) {
      return true;
    }
    return includeTitle && item?.title === normalized;
  };

  static extractEntityIdentifierValue = (searchFields) => {
    if (!searchFields || typeof searchFields !== 'object') return '';
    const value =
      searchFields.graphId || searchFields.identifierId || searchFields.bookId || searchFields.channelsId || '';
    return typeof value === 'string' ? value.trim() : value;
  };

  /**
   * Builds API payload with either graphId or identifierId (never both).
   * Uses entity variant config when provided (EntityIdentifierField graphs variant).
   */
  static buildAuditIdentifierPayload = (searchFields, entityVariantConfig) => {
    if (searchFields?.graphId) {
      return { graphId: searchFields.graphId };
    }
    const value = utils.extractEntityIdentifierValue(searchFields);
    if (!value) return {};
    if (utils.isGraphIdentifierVariant(entityVariantConfig)) {
      return { graphId: value };
    }
    return { identifierId: value };
  };

  static mapAutocompleteSelectionToSearchFields = (newValue, variantConfig) => {
    const bookId = newValue?.bookId || newValue?.graphId || '';
    const channelsId = newValue?.channelsId || '';
    const graphId = newValue?.graphId || (utils.isGraphIdentifierVariant(variantConfig) ? bookId : '');
    const identifierValue = bookId || channelsId || '';

    if (utils.isGraphIdentifierVariant(variantConfig)) {
      return {
        bookId: '',
        channelsId: '',
        identifierId: '',
        graphId: graphId || identifierValue
      };
    }

    return {
      bookId: bookId || '',
      channelsId: channelsId || '',
      graphId: '',
      identifierId: identifierValue
    };
  };

  static syncEntityIdentifierSearchFields = (searchFields, variantConfig, defaultIdentifierId) => {
    const next = { ...searchFields };
    const graphValue = next.graphId || '';
    const bookValue = next.bookId || '';
    const channelValue = next.channelsId || '';
    const defaultValue = defaultIdentifierId || '';

    if (utils.isGraphIdentifierVariant(variantConfig)) {
      const graphId = graphValue || bookValue || defaultValue;
      next.graphId = graphId || '';
      next.bookId = '';
      next.channelsId = '';
      next.identifierId = '';
      return next;
    }

    const identifierId = bookValue || channelValue || defaultValue;
    next.identifierId = identifierId || '';
    next.graphId = '';
    return next;
  };

  /** Prefers bookId over identifierId for trends row display and navigation. */
  static getTrendsRowDisplayId = (row, fallbackId = '') => {
    const bookId = (row?.[constants.BOOK_ID_NAME] || '').trim();
    const identifierId = (row?.[constants.IDENTIFIER_FIELD_NAME_ID] || '').trim();
    const fallback = (fallbackId || '').trim();
    if (bookId) return bookId;
    if (identifierId) return identifierId;
    return fallback;
  };

  static getTrendsRowIdentifierId = (row) => {
    const raw = row?.[constants.IDENTIFIER_FIELD_NAME_ID] ?? row?.[constants.BOOK_ID_NAME];
    return String(raw ?? '').trim();
  };

  /** Title with book id for trends charts and summary (e.g. "Course Name (BRNT-123)"). */
  static formatTrendsIdentifierDisplayLabel = (row, fallbackId = '') => {
    if (!row) return (fallbackId || '').trim();
    const title = (row?.[constants.IDENTIFIER_FIELD_NAME_TITLE] || '').trim();
    const displayId = utils.getTrendsRowDisplayId(row, fallbackId);
    if (title && displayId) {
      return title === displayId ? title : `${title} (${displayId})`;
    }
    return title || displayId;
  };

  static findTrendsRowByIdentifierId = (data, identifierId) => {
    if (!identifierId || !data?.length) return null;
    return data.find((row) => utils.getTrendsRowIdentifierId(row) === identifierId) || null;
  };

  static getFeedbackRowDataPayload(tenantName, row) {
    switch (tenantName) {
      case constants.CHANNELS: {
        const { requestId, bookId, type, userId } = row;
        return { requestId, bookId, type, userId };
      }
      case constants.AI_STUDY_TOOL:
      case constants.PVS:
      default: {
        // Defaults to the aistudytool payload structure
        const { requestId, bookId } = row;
        return { requestId, bookId };
      }
    }
  }
  static getObserveDetailQueryParams = (auditParams) => {
    const { requestId, audioIntent, sdk } = auditParams;
    const { REQUEST_ID, AUDIO_INTENT, SDK } = constants.URL_PARAMS;
    const params = new URLSearchParams({ [REQUEST_ID]: requestId });
    if (audioIntent === true) {
      params.append(AUDIO_INTENT, 'true');
    }
    if (sdk === true) {
      params.append(SDK, 'true');
    }
    const { isStackB, env } = this.getStackBParams();
    if (isStackB) params.append('stackB', 'true');
    if (env) params.append('env', env);
    return params;
  };

  static getObserveRowData(search) {
    const query = new URLSearchParams(search);
    const { REQUEST_ID, TYPE, AUDIO_INTENT, SDK } = constants.URL_PARAMS;
    const audioIntentValue = query.get(AUDIO_INTENT);
    return {
      requestId: query.get(REQUEST_ID),
      ...(query.get(TYPE) && { type: query.get(TYPE) }),
      ...(audioIntentValue === 'true' && { audioIntent: true }),
      ...(query.get(SDK) === 'true' && { sdk: true })
    };
  }

  static getCurrentRowFromContext({ search, userAuditData, auditData, currentSelectedRecordIndex }) {
    const urlRow = utils.getObserveRowData(search);
    const gridRow = userAuditData?.[currentSelectedRecordIndex];
    const apiRow = auditData?.requestId === urlRow?.requestId ? auditData : {};

    if (!_.isEmpty(gridRow)) {
      return { ...urlRow, ...apiRow, ...gridRow };
    }
    return { ...urlRow, ...apiRow };
  }

  static navigateToObserveFromTrendsCellClick(history, tenantName, row, columnId) {
    const bookIdentifier = utils.getTrendsRowDisplayId(row);
    if ((columnId !== constants.BOOK_ID_NAME && columnId !== constants.IDENTIFIER_FIELD_NAME_ID) || !bookIdentifier) {
      return;
    }
    history.push(`/aiadmin/${tenantName}/observe?${constants.BOOK_ID_NAME}=${encodeURIComponent(bookIdentifier)}`);
  }

  static getUrlParam({ isFromTrendsInsights, isFeedbackFlow }) {
    const mapping = [
      { condition: isFromTrendsInsights, value: constants.TRENDS_LABEL },
      { condition: isFeedbackFlow, value: constants.FEEDBACK_URL_LABEL }
    ];

    const match = mapping.find((item) => item.condition);
    return match ? match.value : constants.OBSERVE_LABEL;
  }

  static getFeedbackComments(comment) {
    if (comment == null) return '';
    if (typeof comment === 'string') return comment.trim();
    if (typeof comment === 'object') {
      if ('S' in comment && typeof comment.S === 'string') {
        return comment.S.trim();
      }
      const str = JSON.stringify(comment);
      return str !== '{}' ? str : '';
    }

    return String(comment);
  }

  static getStartDate24HoursAgo() {
    const currentDate = new Date();
    const startDate = new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);
    const month = (startDate.getMonth() + 1).toString().padStart(2, '0');
    const day = startDate.getDate().toString().padStart(2, '0');
    const year = startDate.getFullYear();
    return `${month}/${day}/${year}`;
  }

  static getEndDateTimestamp(endDate = '') {
    let date = new Date();
    if (endDate) {
      date = new Date(endDate);
    }
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999);
  }

  static getStartDateTimestamp(startDate = '') {
    if (!startDate) {
      const currentDate = new Date();
      return Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate(), 0, 0, 0, 0);
    }
    const date = new Date(startDate);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
  }

  static getSevenDaysStartDateTimestamp(type, userId, identifierId, startDate = '') {
    if (!startDate) {
      if (!type && !userId) {
        const currentDate = new Date();
        const sevenDaysAgo = new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        return Date.UTC(
          sevenDaysAgo.getUTCFullYear(),
          sevenDaysAgo.getUTCMonth(),
          sevenDaysAgo.getUTCDate(),
          0,
          0,
          0,
          0
        );
      }
      const currentDate = new Date();
      return Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate(), 0, 0, 0, 0);
    }
    const date = new Date(startDate);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
  }

  static getFormattedStartDate(startDate = '') {
    const date = startDate ? moment(startDate) : moment();
    return date.format(constants.YYYY_MM_DD_START_OF_DAY_FORMAT);
  }

  static getFormattedEndDate(endDate = '') {
    const date = endDate ? moment(endDate) : moment();
    return date.format(constants.YYYY_MM_DD_END_OF_DAY_FORMAT);
  }

  /** Preserves the date/time from the picker (or current moment if no value); does not force end-of-day. */
  static getFormattedEndDateWithTime(endDate = '') {
    const date = endDate ? moment(endDate) : moment();
    return date.format(constants.YYYY_MM_DD_DATETIME_FORMAT);
  }

  /** Formats a date/time in UTC; uses current UTC instant when date is omitted. */
  static getFormattedDateUtc(date = '', format = constants.YYYY_MM_DD_DATETIME_FORMAT) {
    const utcMoment = date ? moment.utc(date) : moment.utc();
    return utcMoment.format(format);
  }

  /** Returns end-of-day UTC string (23:59:59) when date is a past calendar day; otherwise empty string. */
  static getPastDate(date = '') {
    if (!date) return '';
    const day = moment.utc(date).startOf('day');
    if (!day.isValid() || !day.isBefore(moment.utc().startOf('day'))) {
      return '';
    }
    return day.format(constants.YYYY_MM_DD_END_OF_DAY_FORMAT);
  }

  /** Parses stored search/API date strings into a UTC moment for KeyboardDatePicker. */
  static parsePickerValue = (value) => {
    if (!value) return null;
    const parsed = moment.utc(
      value,
      [
        constants.YYYY_MM_DD_DATETIME_FORMAT,
        constants.YYYY_MM_DD_START_OF_DAY_FORMAT,
        constants.YYYY_MM_DD_HYPHEN_FORMAT
      ],
      true
    );
    return parsed.isValid() ? parsed : moment.utc(value);
  };

  static getSevenDaysFormattedStartDate(type, userId, identifierId, startDate = '') {
    if (startDate) {
      return moment(startDate).format('YYYY-MM-DD 00:00:00');
    }
    if (!type && !userId) {
      return moment().subtract(7, 'days').format('YYYY-MM-DD 00:00:00');
    }
    return moment().format('YYYY-MM-DD 00:00:00');
  }

  static formatToDateString(dateStr) {
    if (!dateStr) return '';
    const parsed = moment(dateStr, ['M/D/YYYY, h:mm:ss A', 'ddd, DD MMM YYYY HH:mm:ss [GMT]', moment.ISO_8601]);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  /** Formats a date for UI display, e.g. "01 Jan, 2026". */
  static formatDisplayDate(date, inputFormat = constants.MM_DD_YYYY_FORMAT) {
    if (!date) return '';
    const parsed = moment(date, inputFormat, true);
    return parsed.isValid() ? parsed.format(constants.DISPLAY_DATE_FORMAT) : String(date);
  }

  static TRENDS_DATE_RANGE_OPTIONS = Object.freeze([
    Object.freeze({ key: 'trends-date-range-1d', value: '1D', label: '1d' }),
    Object.freeze({ key: 'trends-date-range-3d', value: '3D', label: '3d' }),
    Object.freeze({ key: 'trends-date-range-1w', value: '1W', label: '1w' })
  ]);

  static TRENDS_DATE_PRESET_OFFSETS = Object.freeze({
    '1D': 0,
    '3D': -2,
    '1W': -6
  });

  static getTrendsPresetDateRange(preset) {
    const offset = utils.TRENDS_DATE_PRESET_OFFSETS[preset];
    if (offset === undefined) {
      return null;
    }
    const today = new Date();
    return {
      startDateTime: moment(today).add(offset, 'days').format(constants.YYYY_MM_DD_FORMAT),
      endDateTime: moment(today).format(constants.YYYY_MM_DD_FORMAT)
    };
  }

  static toEpochUTC(dateStr) {
    if (!dateStr) return null;
    const parsed = moment.utc(dateStr, ['M/D/YYYY, h:mm:ss A', 'ddd, DD MMM YYYY HH:mm:ss [GMT]', moment.ISO_8601]);
    return parsed.isValid() ? parsed.valueOf() : null;
  }

  static epochToDateString(epoch) {
    if (!epoch) return '';
    return moment.utc(epoch).format('YYYY-MM-DD');
  }

  static generateReadableDataFlow(logData) {
    const logs = logData.executionLog;
    const nodes = {};
    const links = [];

    const extractText = (messages) => {
      if (!messages?.length) return '';
      const raw = messages[0].content || messages[0]?.lc_kwargs?.content || '';
      try {
        const parsed = JSON.parse(raw);
        return parsed?.input || raw;
      } catch {
        return raw;
      }
    };

    const friendlyLabel = (name) => {
      if (name === '__start__') return 'Start Node';
      if (name.startsWith('ChannelWrite<branch:to:supervisor')) return 'Send to Supervisor';
      if (name.startsWith('ChannelWrite<branch:to:agent')) return 'Send to Agent';
      if (name.startsWith('ChannelWrite')) return 'Routing Message';
      if (name.startsWith('RunnableSequence')) return 'Internal Agent Chain';
      if (name.startsWith('prompt')) return 'Routing Prompt';
      if (name.startsWith('Branch<')) return 'Branch Decision';
      if (name === 'tools') return 'Tool Group Execution';
      if (name === 'agent') return 'Agent Router';
      if (name === 'supervisor') return 'Supervisor Node';
      if (name === 'LangGraph') return 'Workflow Start';
      // if (name.includes('Explain_Agent')) return 'Explain Agent';
      // if (name.includes('GetRAGTool')) return 'Retrieve Book Content Tool';
      // if (name.includes('GetImageDescription')) return 'Image Description Tool';
      // if (name.includes('GetPriorSessionChatTool')) return 'Previous Chat Context Tool';
      // if (name.includes('transfer_to_explain_agent')) return 'Transfer to Explain Agent Logic';
      return name; // fallback
    };

    // Collect node info
    logs.forEach((log) => {
      const { name, type, data } = log;
      if (!nodes[name]) nodes[name] = {};
      if (type === 'on_chain_start') nodes[name].input = extractText(data.input?.messages);
      if (type === 'on_chain_end') nodes[name].output = extractText(data.output?.messages || data.output);
    });

    // Order nodes based on appearance in logs
    const ordered = Object.entries(nodes).map(([name, val], idx) => ({
      id: idx + 1,
      rawName: name,
      label: friendlyLabel(name),
      input: val.input || '',
      output: val.output || ''
    }));

    // Link nodes based on output → input flow match
    for (let i = 0; i < ordered.length; i++) {
      const currentOutput = ordered[i].output?.trim();
      for (let j = i + 1; j < ordered.length; j++) {
        const nextInput = ordered[j].input?.trim();
        if (currentOutput && nextInput && currentOutput === nextInput) {
          links.push({ from: ordered[i].label, to: ordered[j].label });
          break;
        }
      }
    }

    // Print readable trace
    const res = ordered
      .map((node, index) => {
        const link = links.find((l) => l.from === node.label);
        const arrow = link ? ` → ${link.to}` : '';
        return `🔹 ${node.label}${arrow}\n   👉 Input: ${node.input}\n   🏁 Output: ${node.output}\n`;
      })
      .join('\n');

    return res;
  }

  /**
   * Maps TOC page type to level value for AET payload
   * @param {string} type - The type from TOC page (e.g., 'module', 'sub-section')
   * @returns {string} - The corresponding level value
   */
  static mapTocTypeToLevel(type) {
    const typeToLevelMap = {
      module: 'section',
      slate: 'sub-section'
    };

    return typeToLevelMap[type] || type || '';
  }

  /**
   * Maps viewType value to its corresponding display label
   * @param {string} type - The viewType value (e.g., 'mcq', 'summary', 'aetquiz', 'aettopic')
   * @returns {string} - The corresponding display label
   */
  static getViewTypeLabel(type) {
    const viewTypeLabels = {
      mcq: 'Quiz',
      summary: 'Summary',
      aetquiz: 'AeT Quiz',
      aettopic: 'AeT Topic'
    };
    return viewTypeLabels[type] || type;
  }

  static encodedData(data) {
    const updatedData = new TextEncoder().encode(data);
    const encodedContent = btoa(String.fromCharCode(...updatedData)); //encodeURIComponent(item.content)
    return encodedContent;
  }

  static generateReadableDataFlow(logData) {
    const logs = logData.executionLog;
    const nodes = {};
    const links = [];

    const extractText = (messages) => {
      if (!messages?.length) return '';
      const raw = messages[0].content || messages[0]?.lc_kwargs?.content || '';
      try {
        const parsed = JSON.parse(raw);
        return parsed?.input || raw;
      } catch {
        return raw;
      }
    };

    const friendlyLabel = (name) => {
      if (name === '__start__') return 'Start Node';
      if (name.startsWith('ChannelWrite<branch:to:supervisor')) return 'Send to Supervisor';
      if (name.startsWith('ChannelWrite<branch:to:agent')) return 'Send to Agent';
      if (name.startsWith('ChannelWrite')) return 'Routing Message';
      if (name.startsWith('RunnableSequence')) return 'Internal Agent Chain';
      if (name.startsWith('prompt')) return 'Routing Prompt';
      if (name.startsWith('Branch<')) return 'Branch Decision';
      if (name === 'tools') return 'Tool Group Execution';
      if (name === 'agent') return 'Agent Router';
      if (name === 'supervisor') return 'Supervisor Node';
      if (name === 'LangGraph') return 'Workflow Start';
      // if (name.includes('Explain_Agent')) return 'Explain Agent';
      // if (name.includes('GetRAGTool')) return 'Retrieve Book Content Tool';
      // if (name.includes('GetImageDescription')) return 'Image Description Tool';
      // if (name.includes('GetPriorSessionChatTool')) return 'Previous Chat Context Tool';
      // if (name.includes('transfer_to_explain_agent')) return 'Transfer to Explain Agent Logic';
      return name; // fallback
    };

    // Collect node info
    logs.forEach((log) => {
      const { name, type, data } = log;
      if (!nodes[name]) nodes[name] = {};
      if (type === 'on_chain_start') nodes[name].input = extractText(data.input?.messages);
      if (type === 'on_chain_end') nodes[name].output = extractText(data.output?.messages || data.output);
    });

    // Order nodes based on appearance in logs
    const ordered = Object.entries(nodes).map(([name, val], idx) => ({
      id: idx + 1,
      rawName: name,
      label: friendlyLabel(name),
      input: val.input || '',
      output: val.output || ''
    }));

    // Link nodes based on output → input flow match
    for (let i = 0; i < ordered.length; i++) {
      const currentOutput = ordered[i].output?.trim();
      for (let j = i + 1; j < ordered.length; j++) {
        const nextInput = ordered[j].input?.trim();
        if (currentOutput && nextInput && currentOutput === nextInput) {
          links.push({ from: ordered[i].label, to: ordered[j].label });
          break;
        }
      }
    }

    // Print readable trace
    const res = ordered
      .map((node, index) => {
        const link = links.find((l) => l.from === node.label);
        const arrow = link ? ` → ${link.to}` : '';
        return `🔹 ${node.label}${arrow}\n   👉 Input: ${node.input}\n   🏁 Output: ${node.output}\n`;
      })
      .join('\n');

    return res;
  }

  static base64Encode(string) {
    try {
      const encodedString = encode(utf8.encode(string));
      return encodedString;
    } catch (error) {
      console.error({ name: 'Util', method: 'base64Encode', message: 'base64Encode failed', error });
      throw new Error(error);
    }
  }
  static parseTimeElapsedToSeconds(t) {
    if (typeof t !== 'string') return 0;
    const sMatch = t.match(/^\s*([0-9]+(?:\.[0-9]+)?)\s*s\s*$/i);
    if (sMatch) return parseFloat(sMatch[1]);
    const msMatch = t.match(/^\s*([0-9]+)\s*ms\s*$/i);
    if (msMatch) return parseInt(msMatch[1], 10) / 1000;
    return 0;
  }

  static formatTime(secondsTotal) {
    if (typeof secondsTotal !== 'number' || secondsTotal < 0) return '0ms';

    if (secondsTotal < 1) {
      return `${Math.round(secondsTotal * 1000)}ms`;
    }
    if (secondsTotal < 60) {
      const s = secondsTotal < 10 ? secondsTotal.toFixed(1) : Math.round(secondsTotal);
      return `${s}s`;
    }
    return `${Math.round(secondsTotal / 60)} m`;
  }

  static toArray(maybeArray) {
    if (Array.isArray(maybeArray)) return maybeArray;
    if (maybeArray && typeof maybeArray === 'object') return [maybeArray];
    return [];
  }

  static computeLLMStats(llmTx) {
    {
      const transactions = utils.toArray(llmTx);
      let totalTimeSeconds = 0;
      let totalTokens = 0;
      let totalPromptTokens = 0;
      let totalCompletionTokens = 0;
      let requestCount = 0;
      for (const tx of transactions) {
        const resp = tx?.response || {};
        // Time
        if (resp.timeElapsed) {
          totalTimeSeconds += utils.parseTimeElapsedToSeconds(resp.timeElapsed);
        }
        // Tokens
        const usage = resp?.tokenUsage || {};
        if (usage.promptTokens != null || usage.completionTokens != null) {
          const p = Number(usage.promptTokens || 0);
          const c = Number(usage.completionTokens || 0);
          totalPromptTokens += p;
          totalCompletionTokens += c;
          totalTokens += p + c;
          if (p + c > 0) requestCount++;
        } else if (typeof usage.totalTokens === 'number') {
          totalTokens += usage.totalTokens;
          if (usage.totalTokens > 0) requestCount++;
        }
      }

      const avgTokensPerRequest = requestCount > 0 ? Math.round(totalTokens / requestCount) : 0;

      return {
        totalTimeFormatted: utils.formatTime(totalTimeSeconds),
        totalTokens,
        totalPromptTokens,
        totalCompletionTokens,
        requestCount,
        avgTokensPerRequest
      };
    }
  }

  /**
   * Attempts to parse a string value as JSON, falls back to raw value if parsing fails.
   * Useful for parsing dynamic property values that may contain JSON or plain strings.
   * @param {*} rawValue - The value to parse
   * @returns {*} - Parsed JSON value or the original value if parsing fails
   */
  static parseJsonValue(rawValue) {
    if (typeof rawValue !== 'string') {
      return rawValue;
    }
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return '';
    }
    try {
      return JSON.parse(trimmedValue);
    } catch {
      return rawValue;
    }
  }

  /**
   * Converts any value to its string representation.
   * Handles strings, numbers, booleans, null, and objects.
   * @param {*} value - The value to stringify
   * @returns {string} - String representation of the value
   */
  static stringifyValue(value) {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (value === null) {
      return 'null';
    }
    return JSON.stringify(value);
  }

  /**
   * Parses a JSON string into an object with validation.
   * Throws an error if the input is not a valid JSON object.
   * @param {string} input - JSON string to parse
   * @returns {Object} - Parsed JSON object
   * @throws {Error} - If input is not a valid JSON object
   */
  static parseJsonToObject(input) {
    const trimmed = input?.trim();
    if (!trimmed) {
      return {};
    }
    const parsed = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error(constants.JSON_VALIDATION_ERROR_CODE.JSON_OBJECT_REQUIRED);
    }
    return parsed;
  }

  /**
   * Validates JSON input as an object without throwing.
   * @param {string} input - JSON string to validate
   * @returns {{ valid: boolean, parsed: Object|null, errorCode: string|null, errorMessage: string|null }}
   */
  static validateJsonObject(input) {
    try {
      const parsed = utils.parseJsonToObject(input);
      return { valid: true, parsed, errorCode: null, errorMessage: null };
    } catch (error) {
      return {
        valid: false,
        parsed: null,
        errorCode:
          error.message === constants.JSON_VALIDATION_ERROR_CODE.JSON_OBJECT_REQUIRED
            ? constants.JSON_VALIDATION_ERROR_CODE.JSON_OBJECT_REQUIRED
            : constants.JSON_VALIDATION_ERROR_CODE.PARSE_ERROR,
        errorMessage: error.message === constants.JSON_VALIDATION_ERROR_CODE.JSON_OBJECT_REQUIRED ? null : error.message
      };
    }
  }

  /**
   * Converts an object to an array of key-value pair objects.
   * @param {Object} obj - Object to convert
   * @returns {Array<{key: string, value: string}>} - Array of key-value pair objects
   */
  static objectToKeyValueArray(obj) {
    return Object.entries(obj).map(([key, value]) => ({
      key,
      value: utils.stringifyValue(value)
    }));
  }

  /**
   * Converts an array of key-value pair objects to an object.
   * @param {Array<{key: string, value: string}>} keyValueArray - Array of key-value pairs
   * @returns {Object} - Converted object
   */
  static keyValueArrayToObject(keyValueArray) {
    return keyValueArray.reduce((accumulator, currentItem) => {
      const key = currentItem.key?.trim();
      if (!key) {
        return accumulator;
      }
      accumulator[key] = utils.parseJsonValue(currentItem.value);
      return accumulator;
    }, {});
  }

  /**
   * Validates if a string is a valid Base64-encoded value.
   * Empty or whitespace-only strings are considered valid (field is optional).
   * @param {string} str - The string to validate
   * @returns {boolean} - True if valid Base64 or empty, false otherwise
   */
  static isValidBase64(str) {
    if (!str || str.trim() === '') {
      return true; // Empty value is valid (field is optional)
    }
    try {
      // Check if string matches Base64 pattern
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(str)) {
        return false;
      }
      // Try to decode and re-encode to verify it's valid Base64
      const decoded = atob(str);
      const reEncoded = btoa(decoded);
      return reEncoded === str;
    } catch (e) {
      return false;
    }
  }

  /**
   * Strips markdown code block wrapper if present (```json ... ``` or ``` ... ```).
   * Useful for cleaning AI responses that come wrapped in markdown code blocks.
   * @param {string} str - String that may contain markdown code block wrapper
   * @returns {string} - String with code block wrapper removed
   */
  static stripMarkdownCodeBlock(str) {
    if (!str || typeof str !== 'string') return str;
    let result = str.trim();
    // Remove opening ```json or ``` with optional language identifier
    result = result.replace(/^```(?:json)?\s*\n?/i, '');
    // Remove closing ```
    result = result.replace(/\n?```\s*$/i, '');
    return result.trim();
  }

  /**
   * Formats a JSON object in a readable way without quotes on keys and string values.
   * Useful for displaying JSON data in a human-readable format.
   * @param {*} obj - Object to format
   * @param {number} indent - Current indentation level (default: 0)
   * @returns {string} - Formatted string representation
   */
  static formatJsonReadable(obj, indent = 0) {
    const spaces = '  '.repeat(indent);
    const innerSpaces = '  '.repeat(indent + 1);

    if (obj === null) return 'null';
    if (typeof obj === 'boolean' || typeof obj === 'number') return String(obj);
    if (typeof obj === 'string') return obj;

    if (Array.isArray(obj)) {
      if (obj.length === 0) return '[]';
      const items = obj.map((item) => `${innerSpaces}${utils.formatJsonReadable(item, indent + 1)}`);
      return `[\n${items.join(',\n')}\n${spaces}]`;
    }

    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      if (keys.length === 0) return '{}';
      const entries = keys.map((key) => `${innerSpaces}${key} : ${utils.formatJsonReadable(obj[key], indent + 1)}`);
      return `{\n${entries.join(',\n')}\n${spaces}}`;
    }

    return String(obj);
  }

  static DataEncoded(data) {
    return data.map((item) => ({
      ...item,
      content: this.encodedData(item.content)
    }));
  }

  static getEncodedPayload = (payload) => {
    const updatedPayload = {
      ...payload,
      promptStart: utils.DataEncoded(payload.promptStart),
      promptEnd: utils.DataEncoded(payload.promptEnd)
    };
    return updatedPayload;
  };

  /**
   * Helper to safely extract content string from various formats
   * Handles both: string content and { data: string } object format
   * @param {string|object} content - The content to extract string from
   * @returns {string} - The extracted content string
   */
  static getContentString(content) {
    if (!content) return '';
    if (typeof content === 'string') return content;
    if (typeof content === 'object' && content.data) {
      return typeof content.data === 'string' ? content.data : JSON.stringify(content.data, null, 2);
    }
    return JSON.stringify(content, null, 2);
  }

  /** JSON/string clipboard text; supports MobX observables via toJS. */
  static stringifySafeForCopy(value) {
    try {
      if (value == null) return '';
      if (typeof value === 'string') return value;
      return JSON.stringify(toJS(value), null, 2);
    } catch {
      return String(value);
    }
  }

  /** Plain text for SUMMARY_TYPE botResponse arrays (AI Study Tool copy). */
  static getSummaryTypeBotResponseCopyText(botResponse) {
    if (!Array.isArray(botResponse)) return utils.stringifySafeForCopy(botResponse);
    return botResponse
      .map((response, responseIndex) => {
        const parts = [];
        if (response?.processedSummary?.length) {
          parts.push(`Processed Summary ${responseIndex + 1}\n${response.processedSummary.join('\n\n')}`);
        }
        if (response?.botSummary?.length) {
          parts.push(`Final Summary ${responseIndex + 1}\n${response.botSummary.join('\n\n')}`);
        }
        return parts.join('\n\n');
      })
      .filter(Boolean)
      .join('\n\n');
  }

  /** Flatten common-audit-type object-shaped botResponse for copy. */
  static getCommonAuditBotResponseCopyText(botResponse) {
    if (!botResponse) return '';
    const plain = toJS(botResponse);
    return Object.values(plain)
      .flatMap((item) => (Array.isArray(item) ? item : []))
      .map((sub) => (typeof sub === 'string' ? sub : utils.stringifySafeForCopy(sub)))
      .join('\n\n');
  }

  /** Nested string fields used by explain / salutation / history responses. */
  static getNestedExplainCopyText(botResponse) {
    if (!botResponse || typeof botResponse !== 'object') return '';
    return (
      botResponse.explainStandAloneBotResponse ||
      botResponse.salutationBotResponse ||
      botResponse.answerCyuMcqParserBotResponse ||
      botResponse.historyTitle ||
      botResponse.channelFetch ||
      ''
    );
  }

  /** Clipboard text for subtype Problem Solve Final card. */
  static getSubTypeProblemSolveFinalCopyText(typeData) {
    const psfr = typeData?.botResponse?.problemSolveFinalResponse;
    if (Array.isArray(psfr)) return psfr.join('\n\n');
    if (psfr != null) return String(psfr);
    return utils.stringifySafeForCopy(typeData?.botResponse);
  }

  /** Agentic response list: prefer `agents`, fall back to legacy `botResponseArr`. */
  static getAgenticResponseList(dataValue) {
    const agents = dataValue?.agents;
    if (Array.isArray(agents) && agents.length > 0) {
      return agents;
    }
    return dataValue?.botResponseArr;
  }

  /** Card label for an agentic response item: `displayType`, else a known `name`, else the raw `name`. */
  static getAgenticItemLabel(item) {
    return item?.displayType || constants.AGENT_NAME_DISPLAY_LABELS[item?.name] || item?.name || '';
  }

  /** Parse agent/bot content that may or may not be wrapped in a ```json fence. */
  static parseFencedOrPlainJson(contentStr) {
    if (!contentStr) return null;
    const trimmed = String(contentStr).trim();
    const match = trimmed.match(/^```json([\s\S]*?)```$/);
    try {
      return JSON.parse(match ? match[1].trim() : trimmed);
    } catch {
      return null;
    }
  }

  static parseExplainSuggestionsJson(contentStr) {
    const parsed = utils.parseFencedOrPlainJson(contentStr);
    if (Array.isArray(parsed?.contents)) {
      return parsed.contents.map((entry, idx) => ({
        id: entry?.id ?? idx + 1,
        question: entry?.content || entry?.question || '',
        justification: entry?.justification || ''
      }));
    }
    return [];
  }

  static getExplainSuggestionsItem(data) {
    const fromAgenticList = utils
      .getAgenticResponseList(data)
      ?.find(
        (item) =>
          typeof item === 'object' &&
          (item?.type === constants.EXPLAIN_SUGGESTIONS || item?.name === constants.EXPLAIN_SUGGESTIONS)
      );
    if (fromAgenticList) {
      return fromAgenticList;
    }
    if (data?.type === constants.EXPLAIN_SUGGESTIONS && data?.content != null) {
      return data;
    }
    return null;
  }

  static getExplainSuggestionsList(data) {
    if (!data) return [];

    if (!_.isEmpty(data?.explainSuggestionsResponse)) {
      return data.explainSuggestionsResponse[0]?.suggestions || [];
    }

    const item = utils.getExplainSuggestionsItem(data);
    if (item?.content != null) {
      return utils.parseExplainSuggestionsJson(utils.getContentString(item.content));
    }

    return [];
  }

  static getExplainSuggestionsRawContent(data) {
    const item = utils.getExplainSuggestionsItem(data);
    if (item?.content != null) {
      return utils.getContentString(item.content);
    }
    if (!_.isEmpty(data?.explainSuggestionsResponse)) {
      return utils.stringifySafeForCopy(data.explainSuggestionsResponse);
    }
    return '';
  }

  static getExplainSuggestionsFormattedCopyText(data) {
    const suggestions = utils.getExplainSuggestionsList(data);
    if (suggestions.length > 0) {
      return suggestions
        .map((item) => {
          const questionLine = item?.justification ? `${item?.id} - ${item?.question}` : item?.question;
          return item?.justification
            ? `${questionLine}\n${constants.JUSTIFICATION_LABEL} - ${item.justification}`
            : questionLine;
        })
        .filter(Boolean)
        .join('\n\n');
    }
    return utils.getExplainSuggestionsRawContent(data);
  }

  /** Clipboard text for PromptRenderer main CHAT_PROMPT_CONTENT card. */
  static getPromptRendererCopyValue(dataValue) {
    let copyText = '';
    const addBlock = (text) => {
      if (!text) return;
      copyText += (copyText ? '\n\n' : '') + text;
    };

    if (dataValue?.content) {
      const contentText = Array.isArray(dataValue.content)
        ? dataValue.content.join('\n')
        : utils.getContentString(dataValue.content);
      addBlock(contentText);
    }

    const graph = dataValue?.graphs;
    if (graph?.graphId) {
      addBlock(
        `Graph ID: ${graph.graphId}

Name: ${graph.name ?? ''}

Version: ${graph.version ?? ''}

Status: ${graph.status ?? ''}`
      );
    }

    if (Array.isArray(dataValue?.prompt)) {
      addBlock(
        dataValue.prompt
          .map(
            (p) => `Role: ${p.role}

Prompt:

${p.prompt || ''}`
          )
          .join('\n\n')
      );
    } else if (dataValue?.prompt) {
      addBlock(`Prompt:

${dataValue.prompt}`);
    }

    return copyText.trim();
  }

  /** Clipboard text for PromptRenderer subtype prompt cards (Problem Solve / Python / Final). */
  static getSubTypePromptCopyValue(typeData) {
    let copyText = '';
    const addBlock = (text) => {
      if (!text) return;
      copyText += (copyText ? '\n\n' : '') + text;
    };

    if (typeData?.prompts?.length > 0) {
      addBlock(typeData.prompts.map((p) => `${p.name} Version = ${p.version} (${p.status})`).join('\n'));
    }

    if (Array.isArray(typeData?.prompt)) {
      addBlock(
        typeData.prompt
          .map(
            (p) => `Role: ${p.role}

Prompt:

${p.prompt || ''}`
          )
          .join('\n\n')
      );
    }

    return copyText.trim();
  }

  /**
   * Clipboard text for observe (non-feedback) AI Study Tool response ExpandableCard.
   * @param {{ dataValue: object, checked: object, filterPrompt: string[] }} params
   */
  static getPrimaryResponseCopyText({ dataValue, checked, filterPrompt = [] }) {
    const agenticResponseList = utils.getAgenticResponseList(dataValue);
    if (checked?.aiStudyTools) {
      const src = dataValue?.botResponse ?? agenticResponseList;
      return !_.isEmpty(src) ? JSON.stringify(toJS(src), null, 2) : '';
    }
    if (_.isEmpty(dataValue?.botResponse) && _.isEmpty(agenticResponseList)) {
      return constants.NO_DATA_FOUND;
    }
    const br = dataValue?.botResponse;
    const type = dataValue?.type;

    if (type === constants.CONTENT_FILTER && dataValue?.finalResponse) {
      return String(dataValue.finalResponse);
    }
    if (type === constants.SUMMARY_TYPE) {
      return utils.getSummaryTypeBotResponseCopyText(br);
    }
    if (type === constants.FLASHCARDS && Array.isArray(br)) {
      return br
        .map((data) => {
          const answers = Array.isArray(data.answer) ? data.answer.join('\n') : String(data.answer ?? '');
          return [data.question, answers].filter(Boolean).join('\n');
        })
        .join('\n\n');
    }
    if (
      [
        constants.EXPLAIN_STAND_ALONE,
        constants.SALUTATION,
        constants.ANSWER_CYU_MCQ,
        constants.HISTORY_TITLE,
        constants.CHANNEL_FETCH_TYPE
      ].includes(type) &&
      br
    ) {
      const nested = utils.getNestedExplainCopyText(br);
      if (nested) return String(nested);
    }
    if (filterPrompt.includes(type) && !constants.commonAuditTypesResponse.includes(type) && Array.isArray(br)) {
      return br.join('\n\n');
    }
    if (constants.commonAuditTypesResponse.includes(type) && br) {
      return utils.getCommonAuditBotResponseCopyText(br);
    }
    if (
      [
        constants.CONTEXT_IDENTIFIER_SUMMARY,
        constants.SPECIFIC_TOPIC_INTENT_IDENTIFIER,
        constants.SUMMARY_TOPIC_IDENTIFIER
      ].includes(type) &&
      typeof br === 'object' &&
      br !== null
    ) {
      return `${constants.ROLE_LABEL}: ${br.role}\n${constants.CONTENT_LABEL}: ${utils.getContentString(br.content)}`;
    }
    if (type === constants.CONTEXT_IDENTIFIER_USER_SELECTION && Array.isArray(br)) {
      return br
        .map(
          (d) => `${constants.ROLE_LABEL}: ${d.role}\n${constants.CONTENT_LABEL}: ${utils.getContentString(d.content)}`
        )
        .join('\n\n');
    }
    if ([constants.EXPERTDESK_FREE_RESPONSE, constants.EXPERTDESK_MCQ].includes(type) && br?.expertDeskMcqBotResponse) {
      return utils.stringifySafeForCopy(br.expertDeskMcqBotResponse);
    }
    return typeof br === 'string' ? br : utils.stringifySafeForCopy(br);
  }

  /**
   * Clipboard text for feedback AI Study Tool response ExpandableCard.
   * @param {{ dataValue: object, checked: object, filterPrompt: string[] }} params
   */
  static getFeedbackPrimaryCopyText({ dataValue, checked, filterPrompt = [] }) {
    const agenticResponseList = utils.getAgenticResponseList(dataValue);
    if (checked?.aiStudyTools) {
      const src = dataValue?.botResponse ?? agenticResponseList;
      return !_.isEmpty(src) ? JSON.stringify(toJS(src), null, 2) : '';
    }
    if (_.isEmpty(dataValue?.botResponse) && _.isEmpty(agenticResponseList) && _.isEmpty(dataValue?.feedbackContent)) {
      if (dataValue?.content?.data != null) {
        return utils.stringifySafeForCopy(dataValue.content.data);
      }
      return constants.NO_DATA_FOUND;
    }
    const br = dataValue?.botResponse;
    const type = dataValue?.type;

    if (type === constants.SUMMARY_TYPE) {
      if (!Array.isArray(br)) {
        return utils.stringifySafeForCopy(br);
      }
      return utils.getSummaryTypeBotResponseCopyText(br);
    }
    if (type === constants.FLASHCARDS && Array.isArray(br)) {
      return br
        .map((data) => {
          const answers = Array.isArray(data.answer) ? data.answer.join('\n') : String(data.answer ?? '');
          return [data.question, answers].filter(Boolean).join('\n');
        })
        .join('\n\n');
    }
    if ([constants.EXPLAIN_STAND_ALONE, constants.SALUTATION, constants.ANSWER_CYU_MCQ].includes(type) && br) {
      const nested = utils.getNestedExplainCopyText(br);
      if (nested) return String(nested);
    }
    if (filterPrompt.includes(type) && !constants.commonAuditTypesResponse.includes(type) && Array.isArray(br)) {
      return br.join('\n\n');
    }
    if (constants.commonAuditTypesResponse.includes(type) && br) {
      return utils.getCommonAuditBotResponseCopyText(br);
    }
    if (
      [constants.CONTENT_CLASSIFIER, constants.PROBLEM_SOLVE, constants.PROBLEM_SOLVE_RECTIFIER].includes(type) ||
      type === constants.CHAT_ENRICHMENT
    ) {
      return typeof br === 'string' ? br : utils.stringifySafeForCopy(br);
    }
    return utils.stringifySafeForCopy(br);
  }

  /**
   * Build run-graph URL using env and stack flag.
   */
  static buildRunGraphUrl = ({ env, useStackB, graphId, graphVersion }) => {
    const raw = useStackB ? env.BERLIN_API_URI_STACKB : env.BERLIN_API_URI;
    const baseUrl = (raw || '').replace(/\/+$/, '');
    return `${baseUrl}/api/1.0/run-graph/${graphId}/versions/${graphVersion}`;
  };

  /**
   * Build header lines for cURL command.
   */
  static buildRunGraphHeaderLines = ({
    token,
    tenantHeaderName,
    tenantId,
    threadId,
    locale = 'en-us',
    tenantKey = ''
  }) => {
    const headerLines = [
      `-H "Content-Type: application/json"`,
      `-H "Authorization: Bearer ${token}"`,
      `-H "${tenantHeaderName}: ${tenantId}"`,
      `-H "x-berlin-locale: ${locale}"`,
      `-H "x-berlin-tenant-key: ${tenantKey}"`,
      `-H "useStream: true"`
    ];

    if (!threadId) {
      headerLines.push(`-H "new-thread: true"`);
    } else {
      headerLines.push(`-H "x-thread-id: ${threadId}"`);
    }

    return headerLines;
  };

  /**
   * Build final cURL command string.
   */
  static buildCurlCommand = ({ url, headerLines, payload }) =>
    `curl -X POST "${url}" \\
  ${headerLines.join(' \\\n  ')} \\
  --data-raw '${JSON.stringify(payload, null, 2)}'`;

  /**
   * Trigger file download in the browser from a blob.
   */
  static downloadBlobFile = ({ blob, filename }) => {
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  /**
   * Trigger text file download in the browser.
   */
  static downloadTextFile = ({ filename, text }) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    utils.downloadBlobFile({ blob, filename });
  };

  static handleDownloadCurl = async ({
    env,
    useStackB,
    graphId,
    graphVersion,
    graphName,
    tenantId,
    tenantHeaderName,
    tenantKey = '',
    locale = 'en-us',
    buildPayload,
    getSystemToken,
    getFallbackToken,
    threadId = null
  }) => {
    try {
      const systemToken = await (getSystemToken?.() ?? null);
      const token = systemToken || getFallbackToken?.();
      if (!token) {
        console.error('No token available to generate cURL.');
        return;
      }

      const result = buildPayload?.();
      if (!result || result.error) return;
      const { payload } = result;

      const url = utils.buildRunGraphUrl({ env, useStackB, graphId, graphVersion });
      const headerLines = utils.buildRunGraphHeaderLines({
        token,
        tenantHeaderName,
        tenantId,
        threadId,
        locale,
        tenantKey
      });

      const curlCommand = utils.buildCurlCommand({ url, headerLines, payload });

      const filename = `${graphName}_${graphVersion}-curl.txt`;
      utils.downloadTextFile({ filename, text: curlCommand });
    } catch (err) {
      console.error('Failed to generate Curl:', err);
    }
  };
  /* Removes a trailing display count suffix from cluster names for API path segments.
   * @param {string|null|undefined} clusterName
   * @returns {string}
   */
  static stripClusterNameCountSuffix(clusterName) {
    if (clusterName == null || typeof clusterName !== 'string') return '';
    return clusterName.replace(/\s*\(\d+\)\s*$/, '').trim();
  }

  static getTrendsCountColumnIds(columns) {
    const nonTotalColumnIds = new Set(constants.TRENDS_COUNT_NON_TOTAL_COLUMN_IDS);
    return (columns || [])
      .filter((col) => col?.id && !nonTotalColumnIds.has(col.id) && !col.hideColumn)
      .map((col) => col.id);
  }

  static buildTrendsCountSummary({ data, columns, labels }) {
    if (!data?.length || !columns?.length) {
      return { headerTotalsRow: null, grandTotal: 0, csvLeadingRows: [] };
    }

    const countColumnIds = utils.getTrendsCountColumnIds(columns);
    const totalsMap = countColumnIds.reduce((acc, id) => {
      acc[id] = data.reduce((sum, row) => sum + (Number(row[id]) || 0), 0);
      return acc;
    }, {});
    const grandTotal = Object.values(totalsMap).reduce((sum, val) => sum + val, 0);
    const firstColId = columns.find((col) => !col.hideColumn)?.id;
    const csvLeadingRows = [];

    if (firstColId && labels) {
      csvLeadingRows.push({
        [firstColId]: labels.formatCsvSummary(grandTotal)
      });
      csvLeadingRows.push({ ...totalsMap, [firstColId]: labels.totalRowLabel });
    }

    return {
      headerTotalsRow: totalsMap,
      grandTotal,
      csvLeadingRows
    };
  }

  /** API may send visibleForEntityTypes as a string or an array. */
  static normalizeVisibleForEntityTypes(visibleForEntityTypes) {
    if (!visibleForEntityTypes) {
      return null;
    }
    return Array.isArray(visibleForEntityTypes) ? visibleForEntityTypes : [visibleForEntityTypes];
  }

  static isConfigVisibleForEntityType(config, entityType) {
    const types = utils.normalizeVisibleForEntityTypes(config?.visibleForEntityTypes);
    if (!types?.length) {
      return true;
    }
    return types.includes(entityType);
  }

  /** Maps API action/name values (e.g. reportSearch) to handler action keys. */
  static resolveSearchControlAction(config, aliases) {
    const defaultAliases = {
      reportSearch: constants.SEARCH_ACTION,
      search: constants.SEARCH_ACTION,
      showReport: constants.SHOW_REPORT_ACTION,
      runTest: constants.RUN_TEST_ACTION,
      getResults: constants.GET_RESULTS_ACTION,
      refresh: constants.REFRESH_ACTION,
      reset: constants.RESET_ACTION
    };
    const key = config?.action || config?.name;
    const actionAliases = aliases || defaultAliases;
    return actionAliases[key] || key;
  }

  static runGraphEndPoint(graphIdOrOpts, graphVersion) {
    let graphId;
    let version;
    if (
      arguments.length === 1 &&
      graphIdOrOpts != null &&
      typeof graphIdOrOpts === 'object' &&
      !Array.isArray(graphIdOrOpts)
    ) {
      ({ graphId, graphVersion: version } = graphIdOrOpts);
    } else {
      graphId = graphIdOrOpts;
      version = graphVersion;
    }
    if (graphId == null || graphId === '') return '';
    if (version == null || version === '') return '';
    const v = typeof version === 'number' ? version : parseInt(String(version), 10);
    if (!Number.isFinite(v)) return '';
    return `/api/1.0/run-graph/${graphId}/versions/${v}`;
  }

  /** React ref callback that sets a CSS custom property on a DOM node. */
  static setCssVariableRef(variableName, value) {
    return (node) => {
      if (node) {
        node.style.setProperty(variableName, value);
      }
    };
  }
}
